import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Network, PlusCircle } from 'lucide-react';
import { brokersApi } from '../lib/api-client';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input, Label, FieldHint } from '../components/ui/input';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/dialog';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { LoadingState, ErrorState, EmptyState, friendlyErrorMessage } from '../components/ui/states';
import { useToast } from '../components/ui/toast';
import { can } from '../lib/rbac';
import { useAuth } from '../lib/auth-context';

/**
 * The original "only path to internal targets" broker system (docs/broker.md,
 * apps/api/src/routes/brokers.ts) — predates and is untouched by internalAgentsApi/BUI-1015's
 * lighter-weight internal-testing agent (see Devices.tsx's Network tab), not a duplicate of it.
 * Kept under Advanced nav for whoever still provisions a real mTLS-fronted broker rather than the
 * newer agent.
 */
function RegisterBrokerForm({ onRegistered }: { onRegistered: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [allowedProtocols, setAllowedProtocols] = useState('');
  const [mtlsFingerprint, setMtlsFingerprint] = useState('');
  const [error, setError] = useState<string | null>(null);

  const protocols = allowedProtocols
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p.length > 0);

  const mutation = useMutation({
    mutationFn: () => brokersApi.register({ name, allowedProtocols: protocols, mtlsFingerprint }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brokers'] });
      onRegistered();
    },
    onError: (err) => setError(friendlyErrorMessage(err)),
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="space-y-1">
        <Label htmlFor="broker-name">Name</Label>
        <Input id="broker-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Office broker" required />
      </div>
      <div className="space-y-1">
        <Label htmlFor="broker-protocols">Allowed protocols</Label>
        <Input id="broker-protocols" value={allowedProtocols} onChange={(e) => setAllowedProtocols(e.target.value)} placeholder="tcp, http" required />
        <FieldHint>Comma-separated.</FieldHint>
      </div>
      <div className="space-y-1">
        <Label htmlFor="broker-fingerprint">mTLS certificate fingerprint</Label>
        <Input
          id="broker-fingerprint"
          value={mtlsFingerprint}
          onChange={(e) => setMtlsFingerprint(e.target.value)}
          placeholder="SHA256:AB:CD:..."
          required
        />
        <FieldHint>Supplied by whoever installs the broker — see docs/broker.md.</FieldHint>
      </div>
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      <Button type="submit" className="w-full" disabled={mutation.isPending || !name.trim() || protocols.length === 0 || !mtlsFingerprint.trim()}>
        Register broker
      </Button>
    </form>
  );
}

export function Brokers() {
  const { principal } = useAuth();
  const canManage = principal?.role === undefined || can(principal.role, 'broker:manage');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [registerOpen, setRegisterOpen] = useState(false);
  const query = useQuery({ queryKey: ['brokers'], queryFn: () => brokersApi.list() });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => brokersApi.revoke(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brokers'] });
      toast({ title: 'Broker revoked', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Could not revoke broker', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const brokers = query.data?.brokers ?? [];

  return (
    <div>
      <PageHeader
        title="Brokers"
        description="mTLS-fronted brokers providing the only path to internal targets."
        action={
          canManage ? (
            <Dialog open={registerOpen} onOpenChange={setRegisterOpen}>
              <DialogTrigger asChild>
                <Button>
                  <PlusCircle size={16} /> Register broker
                </Button>
              </DialogTrigger>
              <DialogContent title="Register a broker" description="Record a broker's allowed protocols and mTLS fingerprint.">
                <RegisterBrokerForm onRegistered={() => setRegisterOpen(false)} />
              </DialogContent>
            </Dialog>
          ) : null
        }
      />

      {brokers.length === 0 ? (
        <EmptyState icon={<Network size={32} />} title="No brokers registered yet" description="Register a broker to reach internal targets." />
      ) : (
        <div className="space-y-2">
          {brokers.map((broker) => (
            <Card key={broker.id}>
              <CardContent className="flex items-center justify-between gap-4 pt-6">
                <div className="min-w-0">
                  <p className="truncate font-medium text-ink">{broker.name}</p>
                  <p className="text-xs text-ink-muted">
                    {broker.lastHeartbeatAt ? `Last heartbeat ${new Date(broker.lastHeartbeatAt).toLocaleString()}` : 'No heartbeat yet'} · registered{' '}
                    {new Date(broker.createdAt).toLocaleDateString()}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {broker.allowedProtocols.map((protocol) => (
                      <Badge key={protocol} variant="neutral">
                        {protocol}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge variant={broker.status === 'registered' ? 'success' : 'danger'} className="capitalize">
                    {broker.status}
                  </Badge>
                  {canManage && broker.status === 'registered' ? (
                    <ConfirmDialog
                      trigger={
                        <Button size="sm" variant="danger">
                          Revoke
                        </Button>
                      }
                      title="Revoke this broker?"
                      description={`"${broker.name}" will immediately lose access — internal testing routed through it will stop. This cannot be undone; you'd need to register it again.`}
                      confirmLabel="Revoke broker"
                      variant="danger"
                      onConfirm={() => revokeMutation.mutateAsync(broker.id)}
                    />
                  ) : null}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
